import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import axiosInstance from "../../../api/axiosInstance";

// ─── Theme (matches Pharmacy.jsx) ────────────────────────────────────────────
const C = { gray:'#5B6B79', blue:'#96C5F7', red:'#9E2B25', orange:'#F29E4C', green:'#2CA87F', teal:'#0891b2' }
const thStyle = { fontSize:11, fontWeight:700, color:C.gray, textTransform:'uppercase', letterSpacing:'0.05em', padding:'8px 10px', background:'#f8f9fb', borderBottom:'1px solid #e8ecf0', whiteSpace:'nowrap' }
const tdStyle = { fontSize:13, color:'#2d3748', padding:'7px 10px', verticalAlign:'middle', borderBottom:'1px solid #f0f2f5' }
const actionIcon = (color) => ({ width:30, height:30, borderRadius:8, border:'none', cursor:'pointer', background:`${color}22`, color, display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:15, marginRight:4, flexShrink:0 })
const inputStyle = { width:'100%', height:36, border:'1.5px solid #dde3eb', borderRadius:8, padding:'0 10px', fontSize:13, color:'#1a202c', outline:'none', boxSizing:'border-box' }
const labelStyle = { fontSize:11, fontWeight:600, color:C.gray, display:'block', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.04em' }

const emptyForm = { drug_id:"", batch_number:"", manufacture_date:"", expiry_date:"", quantity_received:"", cost_per_unit:"", vendor_id:"" }

const today = new Date().toISOString().slice(0, 10);

function expiryStatus(expiry) {
  if (!expiry) return null;
  const diff = (new Date(expiry) - new Date()) / (1000 * 60 * 60 * 24);
  if (diff < 0)  return { label:'Expired',    bg:`${C.red}18`,    color:C.red    };
  if (diff <= 30) return { label:'Near Expiry', bg:`${C.orange}18`, color:C.orange };
  return null;
}

export default function Batches() {
  const [batches, setBatches]   = useState([]);
  const [drugs, setDrugs]       = useState([]);
  const [vendors, setVendors]   = useState([]);
  const [search, setSearch]     = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId]     = useState(null);
  const [form, setForm]         = useState(emptyForm);

  const fetchAll = async () => {
    try {
      const [bRes, dRes, vRes] = await Promise.all([
        axiosInstance.get("getAllBatches"),
        axiosInstance.get("getDrugs"),
        axiosInstance.get("getVendors"),
      ]);
      setBatches(bRes.data.data || []);
      setDrugs(dRes.data.data || []);
      setVendors(vRes.data.data || []);
    } catch {
      Swal.fire("Error", "Failed to load batches", "error");
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await axiosInstance.put(`updateBatch/${editId}`, {
          batch_number: form.batch_number, manufacture_date: form.manufacture_date,
          expiry_date: form.expiry_date, quantity_remaining: form.quantity_received,
          cost_per_unit: form.cost_per_unit, vendor_id: form.vendor_id,
        });
        Swal.fire("Success", "Batch updated", "success");
      } else {
        await axiosInstance.post("addBatch", form);
        Swal.fire("Success", "Batch added and stock updated", "success");
      }
      resetForm(); fetchAll();
    } catch { Swal.fire("Error", "Something went wrong!", "error"); }
  };

  const handleEdit = (item) => {
    setForm({
      drug_id: item.drug_id, batch_number: item.batch_number,
      manufacture_date: item.manufacture_date?.slice(0, 10) || "",
      expiry_date: item.expiry_date?.slice(0, 10) || "",
      quantity_received: item.quantity_remaining,
      cost_per_unit: item.cost_per_unit || "", vendor_id: item.vendor_id || "",
    });
    setEditId(item.id); setEditMode(true); setModalOpen(true);
  };

  const handleDelete = async (id) => {
    const ok = await Swal.fire({ title:"Are you sure?", text:"Batch will be deleted.", icon:"warning", showCancelButton:true, confirmButtonText:"Yes, delete it!" });
    if (ok.isConfirmed) {
      try { await axiosInstance.delete(`deleteBatch/${id}`); Swal.fire("Deleted!", "Batch deleted.", "success"); fetchAll(); }
      catch { Swal.fire("Error", "Something went wrong!", "error"); }
    }
  };

  const resetForm = () => { setForm(emptyForm); setEditMode(false); setEditId(null); setModalOpen(false); };

  const filtered = batches.filter(b =>
    b.drug_name?.toLowerCase().includes(search.toLowerCase()) ||
    b.batch_number?.toLowerCase().includes(search.toLowerCase()) ||
    b.vendor_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      {/* ── Toolbar ── */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8, marginBottom:14 }}>
        <div style={{ position:'relative', maxWidth:340 }}>
          <i className="ti ti-search" style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:C.gray, fontSize:14, pointerEvents:'none' }} />
          <input
            type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by drug, batch #, vendor..."
            style={{ ...inputStyle, paddingLeft:32, maxWidth:340 }}
          />
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <span style={{ fontSize:11, color:C.gray }}>{filtered.length} record{filtered.length !== 1 ? 's' : ''}</span>
          <button className="bgBtn btn-sm d-flex align-items-center gap-1" style={{ fontSize:13, padding:'5px 12px' }} onClick={() => setModalOpen(true)}>
            <i className="ti ti-plus" style={{ fontSize:14 }} /> Add Batch
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      <div style={{ overflowX:'auto', borderRadius:8, border:'1px solid #e8ecf0' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, borderLeft:`3px solid ${C.teal}` }}>#</th>
              <th style={thStyle}>Drug</th>
              <th style={thStyle}>Batch #</th>
              <th style={thStyle}>Mfg Date</th>
              <th style={thStyle}>Expiry Date</th>
              <th style={thStyle}>Qty Received</th>
              <th style={thStyle}>Qty Remaining</th>
              <th style={thStyle}>Cost / Unit</th>
              <th style={thStyle}>Vendor</th>
              <th style={{ ...thStyle, textAlign:'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ ...tdStyle, textAlign:'center', color:C.gray, padding:'36px 0' }}>
                  <i className="ti ti-database-off" style={{ fontSize:28, display:'block', marginBottom:6, opacity:0.4 }} />
                  No batches found
                </td>
              </tr>
            ) : filtered.map((item, i) => {
              const exp = expiryStatus(item.expiry_date);
              const qtyColor = item.quantity_remaining === 0 ? C.red : item.quantity_remaining <= 5 ? C.orange : C.green;
              return (
                <tr
                  key={item.id}
                  style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}
                  onMouseEnter={e => e.currentTarget.style.background = `${C.teal}0d`}
                  onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafbfc'}
                >
                  <td style={tdStyle}>{i + 1}</td>
                  <td style={{ ...tdStyle, fontWeight:500 }}>{item.drug_name}</td>
                  <td style={tdStyle}>
                    <span style={{ fontFamily:'monospace', fontSize:12, background:'#f1f3f5', padding:'2px 6px', borderRadius:4 }}>{item.batch_number}</span>
                  </td>
                  <td style={tdStyle}>{item.manufacture_date?.slice(0, 10) || '—'}</td>
                  <td style={tdStyle}>
                    <span style={{ color: exp ? exp.color : 'inherit' }}>{item.expiry_date?.slice(0, 10) || '—'}</span>
                    {exp && <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:8, background:exp.bg, color:exp.color, marginLeft:6 }}>{exp.label}</span>}
                  </td>
                  <td style={tdStyle}>{item.quantity_received}</td>
                  <td style={tdStyle}>
                    <span style={{ fontSize:12, fontWeight:700, padding:'2px 8px', borderRadius:10, background:`${qtyColor}18`, color:qtyColor }}>{item.quantity_remaining}</span>
                  </td>
                  <td style={tdStyle}>{item.cost_per_unit ? Number(item.cost_per_unit).toFixed(3) : '—'}</td>
                  <td style={tdStyle}>{item.vendor_name || '—'}</td>
                  <td style={{ ...tdStyle, textAlign:'center', whiteSpace:'nowrap' }}>
                    <button onClick={() => setViewItem(item)} style={actionIcon(C.blue)}  title="View"><i className="ti ti-eye"   /></button>
                    <button onClick={() => handleEdit(item)}  style={actionIcon(C.orange)} title="Edit"><i className="ti ti-edit"  /></button>
                    <button onClick={() => handleDelete(item.id)} style={actionIcon(C.red)} title="Delete"><i className="ti ti-trash" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Add / Edit Modal ── */}
      {modalOpen && (
        <div style={{ position:'fixed', inset:0, zIndex:1050, background:'rgba(0,0,0,0.35)', display:'flex', alignItems:'center', justifyContent:'center' }} onClick={resetForm}>
          <div style={{ background:'#fff', borderRadius:12, width:'90%', maxWidth:720, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 8px 32px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding:'16px 24px', borderBottom:'1px solid #e8ecf0', display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ width:32, height:32, borderRadius:8, background:`${C.teal}18`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <i className={`ti ${editMode ? 'ti-edit' : 'ti-plus'}`} style={{ color:C.teal, fontSize:16 }} />
              </div>
              <div style={{ fontSize:14, fontWeight:700, color:'#1a202c' }}>{editMode ? 'Edit Batch' : 'Add Batch'}</div>
              <button onClick={resetForm} style={{ marginLeft:'auto', width:26, height:26, borderRadius:6, border:'1px solid #e8ecf0', background:'#f8f9fb', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:C.gray, fontSize:14 }}>
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ padding:'20px 24px' }}>
                <div style={{ display:'flex', flexWrap:'wrap', gap:12 }}>
                  {/* Drug */}
                  <div style={{ flex:'1 1 200px' }}>
                    <label style={labelStyle}>Drug <span style={{ color:C.red }}>*</span></label>
                    <select name="drug_id" value={form.drug_id} onChange={handleChange} required disabled={editMode}
                      style={{ ...inputStyle, background: editMode ? '#f8f9fb' : '#fff' }}
                      onFocus={e => e.target.style.borderColor = C.teal} onBlur={e => e.target.style.borderColor = '#dde3eb'}>
                      <option value="">-- Select Drug --</option>
                      {drugs.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                  {/* Batch Number */}
                  <div style={{ flex:'1 1 200px' }}>
                    <label style={labelStyle}>Batch Number <span style={{ color:C.red }}>*</span></label>
                    <input name="batch_number" value={form.batch_number} onChange={handleChange} required style={inputStyle}
                      onFocus={e => e.target.style.borderColor = C.teal} onBlur={e => e.target.style.borderColor = '#dde3eb'} />
                  </div>
                  {/* Mfg Date */}
                  <div style={{ flex:'1 1 200px' }}>
                    <label style={labelStyle}>Manufacture Date</label>
                    <input type="date" name="manufacture_date" value={form.manufacture_date} onChange={handleChange} max={today} style={inputStyle}
                      onFocus={e => e.target.style.borderColor = C.teal} onBlur={e => e.target.style.borderColor = '#dde3eb'} />
                  </div>
                  {/* Expiry Date */}
                  <div style={{ flex:'1 1 200px' }}>
                    <label style={labelStyle}>Expiry Date</label>
                    <input type="date" name="expiry_date" value={form.expiry_date} onChange={handleChange} style={inputStyle}
                      onFocus={e => e.target.style.borderColor = C.teal} onBlur={e => e.target.style.borderColor = '#dde3eb'} />
                  </div>
                  {/* Quantity */}
                  <div style={{ flex:'1 1 200px' }}>
                    <label style={labelStyle}>{editMode ? 'Qty Remaining' : 'Qty Received'} <span style={{ color:C.red }}>*</span></label>
                    <input type="number" name="quantity_received" value={form.quantity_received} onChange={handleChange} required min="1" style={inputStyle}
                      onFocus={e => e.target.style.borderColor = C.teal} onBlur={e => e.target.style.borderColor = '#dde3eb'} />
                  </div>
                  {/* Cost */}
                  <div style={{ flex:'1 1 200px' }}>
                    <label style={labelStyle}>Cost Per Unit</label>
                    <input type="number" step="0.001" name="cost_per_unit" value={form.cost_per_unit} onChange={handleChange} min="0" style={inputStyle}
                      onFocus={e => e.target.style.borderColor = C.teal} onBlur={e => e.target.style.borderColor = '#dde3eb'} />
                  </div>
                  {/* Vendor */}
                  <div style={{ flex:'1 1 200px' }}>
                    <label style={labelStyle}>Vendor</label>
                    <select name="vendor_id" value={form.vendor_id} onChange={handleChange} style={inputStyle}
                      onFocus={e => e.target.style.borderColor = C.teal} onBlur={e => e.target.style.borderColor = '#dde3eb'}>
                      <option value="">-- Select Vendor --</option>
                      {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </select>
                  </div>
                </div>
              </div>
              <div style={{ padding:'14px 24px', borderTop:'1px solid #e8ecf0', display:'flex', justifyContent:'flex-end', gap:8 }}>
                <button type="button" onClick={resetForm} style={{ padding:'7px 16px', fontSize:13, borderRadius:8, border:'1px solid #dde3eb', background:'#f8f9fb', color:C.gray, cursor:'pointer' }}>Cancel</button>
                <button type="submit" className="bgBtn btn-sm" style={{ padding:'7px 16px', fontSize:13 }}>{editMode ? 'Update Batch' : 'Add Batch'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── View Modal ── */}
      {viewItem && (() => {
        const exp = expiryStatus(viewItem.expiry_date);
        return (
          <div style={{ position:'fixed', inset:0, zIndex:1050, background:'rgba(0,0,0,0.35)', display:'flex', alignItems:'center', justifyContent:'center' }} onClick={() => setViewItem(null)}>
            <div style={{ background:'#fff', borderRadius:12, width:480, padding:24, boxShadow:'0 8px 32px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:18 }}>
                <div style={{ width:34, height:34, borderRadius:8, background:`${C.teal}18`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <i className="ti ti-pill" style={{ color:C.teal, fontSize:18 }} />
                </div>
                <div style={{ fontSize:14, fontWeight:700, color:'#1a202c' }}>Batch Details</div>
                <button onClick={() => setViewItem(null)} style={{ marginLeft:'auto', width:26, height:26, borderRadius:6, border:'1px solid #e8ecf0', background:'#f8f9fb', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:C.gray, fontSize:14 }}>
                  <i className="ti ti-x" />
                </button>
              </div>
              <div style={{ borderTop:'1px solid #f0f2f5', paddingTop:14 }}>
                {[
                  ['Drug',          viewItem.drug_name],
                  ['Batch Number',  viewItem.batch_number],
                  ['Mfg Date',      viewItem.manufacture_date?.slice(0, 10)],
                  ['Expiry Date',   viewItem.expiry_date?.slice(0, 10)],
                  ['Qty Received',  viewItem.quantity_received],
                  ['Qty Remaining', viewItem.quantity_remaining],
                  ['Cost / Unit',   viewItem.cost_per_unit ? Number(viewItem.cost_per_unit).toFixed(3) : null],
                  ['Vendor',        viewItem.vendor_name],
                ].map(([label, val]) => (
                  <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid #f5f7fa', fontSize:13 }}>
                    <span style={{ color:C.gray, fontWeight:600 }}>{label}</span>
                    <span style={{ color:'#2d3748', textAlign:'right', maxWidth:'60%' }}>
                      {label === 'Expiry Date' && exp
                        ? <><span style={{ color:exp.color }}>{val || '—'}</span> <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:8, background:exp.bg, color:exp.color }}>{exp.label}</span></>
                        : (val ?? '—')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
}
